import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, PlayCircle, Mic, ShieldCheck, Sparkles, FileText } from "lucide-react";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/context/ThemeContext";
import { useAuth } from "@/context/AuthContext";
import { Features, Demo, Steps } from "@/components/landing/LandingSections";
import { PricingSection, Faq, Footer } from "@/components/landing/LandingMore";

const NAV = [["Fonctionnalités", "#fonctionnalites"], ["Démonstration", "#demo"], ["Offres", "#offres"], ["FAQ", "#faq"]];

const Nav = () => {
  const { user } = useAuth();
  return (
    <header className="fixed inset-x-0 top-0 z-50 px-4 pt-4">
      <nav className="glass mx-auto flex max-w-6xl items-center justify-between rounded-2xl px-4 py-2.5 shadow-sm">
        <Logo />
        <div className="hidden items-center gap-7 text-sm text-muted-foreground md:flex">
          {NAV.map(([l, h]) => (
            <a key={h} href={h} data-testid={`nav-link-${h.slice(1)}`} className="transition-colors hover:text-foreground">{l}</a>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <ThemeToggle />
          <Link
            to={user ? "/app" : "/connexion"}
            data-testid="nav-cta-button"
            className="rounded-full bg-foreground px-4 py-2 text-sm font-semibold text-background transition-opacity hover:opacity-85"
          >
            {user ? "Mon espace" : "Se connecter"}
          </Link>
        </div>
      </nav>
    </header>
  );
};

const HeroPreview = () => (
  <motion.div
    initial={{ opacity: 0, y: 30, rotate: 1.5 }}
    animate={{ opacity: 1, y: 0, rotate: 0 }}
    transition={{ duration: 0.9, delay: 0.25, ease: [0.2, 0.7, 0.2, 1] }}
    className="relative"
  >
    <div className="glass relative z-10 rounded-3xl p-5 shadow-[0_30px_80px_-40px_hsl(var(--primary)/0.55)]">
      <div className="flex items-center justify-between">
        <span className="inline-flex items-center gap-2 rounded-full bg-red-500/10 px-3 py-1 text-xs font-semibold text-red-600 dark:text-red-400">
          <span className="h-2 w-2 animate-pulse rounded-full bg-red-500" /> Micro actif · 00:24:18
        </span>
        <span className="eyebrow">Aperçu</span>
      </div>
      <div className="mt-5 flex h-16 items-end gap-1">
        {Array.from({ length: 42 }).map((_, i) => (
          <span key={i} className="eq-bar bg-brand w-full rounded-full" style={{ height: `${25 + ((i * 37) % 70)}%`, animationDelay: `${(i % 9) * 0.11}s` }} />
        ))}
      </div>
      <p className="mt-5 text-sm leading-relaxed text-muted-foreground">
        « …en 1789, les États généraux sont convoqués par Louis XVI parce que le royaume fait face à une grave crise financière… »
      </p>
    </div>
    <div className="card-flat relative z-20 -mt-6 ml-8 mr-[-8px] p-5 shadow-xl sm:ml-16">
      <p className="eyebrow mb-2 flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5 text-primary" /> Notes générées</p>
      <h4 className="font-display text-base font-bold">HISTOIRE — LA RÉVOLUTION FRANÇAISE</h4>
      <ul className="mt-2 space-y-1.5 text-sm">
        <li><span className="font-semibold">2. Contexte historique</span> — <span className="text-muted-foreground">crise financière, <b className="text-foreground">États généraux</b></span></li>
        <li><span className="font-semibold">6. Dates à retenir</span> — <span className="text-muted-foreground"><b className="text-foreground">5 mai 1789</b>, 14 juillet 1789</span></li>
      </ul>
    </div>
    <div className="glow -right-10 top-10 h-64 w-64 bg-violet-500/30" />
    <div className="glow -left-10 bottom-0 h-56 w-56 bg-blue-500/30" />
  </motion.div>
);

const Hero = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  return (
    <section className="relative overflow-hidden px-5 pb-20 pt-36 sm:pt-44">
      <div className="glow left-1/3 top-10 h-[420px] w-[520px] bg-blue-400/25 dark:bg-blue-600/20" />
      <div className="relative mx-auto grid max-w-6xl items-center gap-14 lg:grid-cols-[1.15fr_1fr]">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <span className="inline-flex items-center gap-2 rounded-full border bg-card/70 px-3 py-1.5 text-xs font-medium text-muted-foreground">
            <ShieldCheck className="h-3.5 w-3.5 text-primary" /> Enregistrement toujours visible et autorisé
          </span>
          <h1 data-testid="hero-title" className="mt-6 text-4xl font-extrabold leading-[1.02] tracking-tighter sm:text-5xl lg:text-6xl">
            Ton cours se transforme en notes. <span className="text-brand">Automatiquement.</span>
          </h1>
          <p data-testid="hero-subtitle" className="mt-6 max-w-xl text-base text-muted-foreground md:text-lg">
            Enregistre ton cours avec autorisation et laisse l'IA créer tes notes, résumés et fiches de révision.
          </p>
          <div className="mt-9 flex flex-wrap items-center gap-3">
            <button
              data-testid="hero-start-free-button"
              onClick={() => navigate(user ? "/app" : "/connexion")}
              className="bg-brand group inline-flex items-center gap-2 rounded-full px-6 py-3.5 text-sm font-semibold text-white shadow-[0_14px_30px_-12px_hsl(var(--primary)/0.8)] transition-transform duration-200 hover:-translate-y-0.5 active:scale-[0.98]"
            >
              Commencer gratuitement <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </button>
            <a
              href="#demo"
              data-testid="hero-demo-button"
              className="inline-flex items-center gap-2 rounded-full border bg-card/70 px-6 py-3.5 text-sm font-semibold transition-colors hover:border-primary/40 hover:text-primary"
            >
              <PlayCircle className="h-4 w-4" /> Voir une démonstration
            </a>
          </div>
          <div className="mt-10 flex flex-wrap gap-6 text-sm text-muted-foreground">
            <span className="inline-flex items-center gap-2"><Mic className="h-4 w-4 text-primary" /> Cours de 30 à 90 min</span>
            <span className="inline-flex items-center gap-2"><FileText className="h-4 w-4 text-primary" /> Notes, fiches et quiz</span>
          </div>
        </motion.div>
        <HeroPreview />
      </div>
    </section>
  );
};

export default function Landing() {
  return (
    <div className="grain relative min-h-screen overflow-x-hidden" data-testid="landing-page">
      <Nav />
      <Hero />
      <Features />
      <Demo />
      <Steps />
      <PricingSection />
      <Faq />
      <Footer />
    </div>
  );
}
